import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { ArrowLeft, ArrowRight, ChartBar, X } from "@phosphor-icons/react";
import { format, parseISO, subMonths } from "date-fns";
import { ASSESSMENT_DISPLAY_NAMES, AssessmentType } from "@constants/assessmentTypes";
import {
  AssessmentStatus,
  PayorRequiredAssessmentsRule,
} from "hooks/api/documentGeneration/useGetDocumentChecklist";
import OverrideChip from "./OverrideChip";

type DerivedSection = "included" | "missing" | "available";

// Types where `hasMostRecentGrid` is meaningful and a false value means the grid needs to be attached before inclusion.
const GRID_RELEVANT_TYPES: ReadonlyArray<AssessmentType> = [
  "AFLS",
  "SOCIALLY_SAVVY",
  "CPFQ_CHILD",
  "CPFQ_CAREGIVER",
];

const isGridIncomplete = (item: AssessmentStatus): boolean =>
  GRID_RELEVANT_TYPES.includes(item.type) && item.hasMostRecentGrid === false;

const needsGridReadmin = (item: AssessmentStatus): boolean =>
  isGridIncomplete(item) && Boolean(item.lastCompletedAt) && item.isUpToDate;

const isCaregiverQabf = (item: AssessmentStatus): boolean =>
  item.type === "QABF" && item.isRequired === false;

interface ReviewAssessmentsStepProps {
  /** Whether an active override waives this document's readiness checks */
  isOverridden?: boolean;
  /** Display title of the document type */
  documentTitle: string;
  /** Client ID — used for the "Go to" deep links into the Assessments page */
  clientId: string;
  /** Assessment types that are required for this document; others render as Available */
  requiredAssessmentTypes: AssessmentType[];
  /** Full assessment list for the document */
  assessments: AssessmentStatus[];
  /** Payor (insurance) rule layered on top of the static doc-type requirements; null when no rule applies */
  payorRequiredAssessments: PayorRequiredAssessmentsRule | null;
  /** Whether the assessments are still loading */
  isLoading: boolean;
  /** Reports the distinct assessment types currently in the "Included" section. */
  onIncludedAssessmentsChange: (types: AssessmentType[]) => void;
  /** Reports the assessmentIds of administered caregiver QABF rows the clinician has deselected. */
  onExcludedQabfIdsChange: (assessmentIds: number[]) => void;
  /** Callback to return to the previous step */
  onBack: () => void;
  /** Callback to close the dialog */
  onClose: () => void;
  /** Callback when the user confirms the assessment selections */
  onContinue: () => void;
}

const SECTION_TITLES: Record<
  DerivedSection,
  { title: string; iconColor: string; caption?: string }
> = {
  included: { title: "Included assessments", iconColor: "success.main" },
  missing: { title: "Missing assessments", iconColor: "error.main" },
  available: {
    title: "Available assessments",
    iconColor: "text.secondary",
    caption: "You can only add assessments you've administered",
  },
};

const getSection = (
  item: AssessmentStatus,
  isRequired: boolean,
  isPayorRule: boolean,
  isUserSelected: boolean,
  payorOperator: "AND" | "OR" | null,
  isPayorRuleSatisfied: boolean
): DerivedSection | null => {
  // A payor OR rule already satisfied by another member: hide unusable members
  // (stale or never administered) instead of blocking in Missing.
  const orSatisfied = payorOperator === "OR" && isPayorRuleSatisfied;
  if (!item.lastCompletedAt) {
    // Required assessments (including per-goal QABF) that have never been administered.
    if (isRequired) return "missing";
    if (isPayorRule) return orSatisfied ? null : "missing";
    return null;
  }
  if (isRequired) return item.isUpToDate ? "included" : "missing";
  if (isPayorRule) {
    if (!item.isUpToDate) return orSatisfied ? null : "missing";
    if (needsGridReadmin(item)) return "missing";
    if (isUserSelected) return "included";
    // Deselected and up-to-date: OR + rule still valid → Available; otherwise → Missing
    if (payorOperator === "OR" && isPayorRuleSatisfied) return "available";
    return "missing";
  }
  return isUserSelected ? "included" : "available";
};

const getIncludedReason = (
  item: AssessmentStatus,
  documentTitle: string,
  isPayorRule: boolean,
  isRequired: boolean
): string => {
  if (item.type === "QABF" && item.relatedGoal) {
    return `Required for behavior reduction goal: ${item.relatedGoal}`;
  }
  if (isPayorRule && !isRequired) return "Required by Client's Insurance";
  return `Required for ${documentTitle}`;
};

const getMissingReason = (item: AssessmentStatus): React.ReactNode | null => {
  if (item.type === "QABF" && item.relatedGoal) {
    return (
      <>
        The assessment is required for the behavior reduction goal:{" "}
        <Box component="span" sx={{ color: "primary.main", fontWeight: 500 }}>
          {item.relatedGoal}
        </Box>
      </>
    );
  }
  if (item.lastCompletedAt && !item.isUpToDate) {
    return "This assessment must be completed within 60 days before your authorization expires. Complete a new assessment to meet insurance requirements.";
  }
  if (needsGridReadmin(item)) {
    return `${ASSESSMENT_DISPLAY_NAMES[item.type]} Scoring Grid for most recent administration is required`;
  }
  if (!item.lastCompletedAt) {
    return "Not administered";
  }
  return null;
};

/**
 * Review Assessments step of the Generate Document dialog.
 * Shown after the preflight check is resolved.
 * Lets the user review which clinical assessments will be included in the document.
 */
const ReviewAssessmentsStep: React.FC<ReviewAssessmentsStepProps> = ({
  documentTitle,
  clientId,
  requiredAssessmentTypes,
  assessments,
  payorRequiredAssessments,
  isLoading,
  onIncludedAssessmentsChange,
  onExcludedQabfIdsChange,
  onBack,
  onClose,
  onContinue,
  isOverridden = false,
}) => {
  const requiredSet = new Set(requiredAssessmentTypes);
  const payorRuleSet = new Set(payorRequiredAssessments?.assessmentTypes ?? []);

  // Controlled accordion expand state — prevents desync when toggling checkboxes re-renders the tree.
  const [expandedSections, setExpandedSections] = useState(
    new Set<DerivedSection>(["included", "missing"])
  );

  const toggleExpanded = (section: DerivedSection) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const [selectedOptional, setSelectedOptional] = useState(new Set<AssessmentType>());
  const [excludedQabfIds, setExcludedQabfIds] = useState(new Set<number>());

  // Default-select optional assessments completed within the last 6 months, once on load.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current || assessments.length === 0) return;
    hydratedRef.current = true;
    const cutoff = subMonths(new Date(), 6);
    const initial = new Set<AssessmentType>();
    const initialExcludedQabf = new Set<number>();
    for (const a of assessments) {
      if (!a.lastCompletedAt) continue;
      if (isCaregiverQabf(a)) {
        if (a.assessmentId != null && new Date(a.lastCompletedAt) < cutoff) {
          initialExcludedQabf.add(a.assessmentId);
        }
        continue;
      }
      if (requiredSet.has(a.type)) continue;
      if (payorRuleSet.has(a.type)) {
        if (a.isUpToDate) initial.add(a.type);
        continue;
      }
      if (new Date(a.lastCompletedAt) >= cutoff) initial.add(a.type);
    }
    setSelectedOptional(initial);
    setExcludedQabfIds(initialExcludedQabf);
  }, [assessments]);

  const toggleOptional = (type: AssessmentType) => {
    setSelectedOptional((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  const toggleQabfExclusion = (assessmentId: number) => {
    setExcludedQabfIds((prev) => {
      const next = new Set(prev);
      if (next.has(assessmentId)) next.delete(assessmentId);
      else next.add(assessmentId);
      return next;
    });
  };

  const assessmentByType = new Map(assessments.map((a) => [a.type, a]));
  const isTypeValidlyIncluded = (t: AssessmentType): boolean => {
    const a = assessmentByType.get(t);
    if (!a?.lastCompletedAt || !a?.isUpToDate) return false;
    if (isGridIncomplete(a)) return false;
    if (requiredSet.has(t)) return true;
    return selectedOptional.has(t);
  };
  const isPayorRuleSatisfied = !payorRequiredAssessments
    ? true
    : payorRequiredAssessments.operator === "AND"
      ? payorRequiredAssessments.assessmentTypes.every(isTypeValidlyIncluded)
      : payorRequiredAssessments.assessmentTypes.some(isTypeValidlyIncluded);

  const grouped: Record<DerivedSection, AssessmentStatus[]> = {
    included: [],
    missing: [],
    available: [],
  };
  for (const item of assessments) {
    if (isGridIncomplete(item)) {
      if (!payorRuleSet.has(item.type)) continue;
      if (payorRequiredAssessments?.operator === "OR" && isPayorRuleSatisfied) continue;
    }
    const isUserSelected = isCaregiverQabf(item)
      ? item.assessmentId != null && !excludedQabfIds.has(item.assessmentId)
      : selectedOptional.has(item.type);
    const section = getSection(
      item,
      item.isRequired !== false && requiredSet.has(item.type),
      payorRuleSet.has(item.type),
      isUserSelected,
      payorRequiredAssessments?.operator ?? null,
      isPayorRuleSatisfied
    );
    if (section) grouped[section].push(item);
  }
  // Pin static-required assessments to the top of Included in the order Vineland, CFQL2, QABF.
  const INCLUDED_PRIORITY: AssessmentType[] = ["VINELAND", "CFQL2", "QABF"];
  grouped.included.sort((a, b) => {
    const ai = INCLUDED_PRIORITY.indexOf(a.type);
    const bi = INCLUDED_PRIORITY.indexOf(b.type);
    const aRank = ai === -1 ? INCLUDED_PRIORITY.length : ai;
    const bRank = bi === -1 ? INCLUDED_PRIORITY.length : bi;
    return aRank - bRank;
  });
  const sections = (Object.keys(SECTION_TITLES) as DerivedSection[]).map((section) => ({
    section,
    ...SECTION_TITLES[section],
    items: grouped[section],
  }));

  const renderRow = (item: AssessmentStatus, section: DerivedSection) => {
    const displayName = ASSESSMENT_DISPLAY_NAMES[item.type];
    const caregiverQabf = isCaregiverQabf(item);
    const isRequired = requiredSet.has(item.type) && !caregiverQabf;
    const isPayorRule = payorRuleSet.has(item.type);
    const showCheckbox = isRequired
      ? false
      : (section === "included" ||
          section === "available" ||
          (isPayorRule &&
            section === "missing" &&
            Boolean(item.lastCompletedAt) &&
            item.isUpToDate &&
            !isGridIncomplete(item))) &&
        (!caregiverQabf || item.assessmentId != null);
    const missingReason = section === "missing" ? getMissingReason(item) : null;
    const showIncludedReason = section === "included" && (isRequired || isPayorRule);
    return (
      <Stack
        key={`${item.type}__${item.relatedGoal ?? ""}`}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 4,
          py: 3,
          borderRadius: "8px",
          gap: 3,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        {showCheckbox && (
          <Checkbox
            checked={isPayorRule ? selectedOptional.has(item.type) : section === "included"}
            onClick={(e) => e.stopPropagation()}
            onChange={() => {
              if (caregiverQabf) {
                if (item.assessmentId != null) toggleQabfExclusion(item.assessmentId);
              } else {
                toggleOptional(item.type);
              }
            }}
            sx={{ flexShrink: 0 }}
            inputProps={{
              "aria-label":
                caregiverQabf && item.relatedGoal
                  ? `Include QABF for ${item.relatedGoal} in document`
                  : `Include ${displayName} in document`,
            }}
          />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1">{displayName}</Typography>
          {caregiverQabf && item.relatedGoal && (
            <Typography variant="body2" color="text.secondary">
              {item.relatedGoal}
            </Typography>
          )}
          {item.lastCompletedAt && (
            <Typography variant="body2" color="text.secondary">
              Last assessment: {format(parseISO(item.lastCompletedAt.slice(0, 10)), "MMM do, yyyy")}
            </Typography>
          )}
          {missingReason && (
            <Typography variant="body2" color="text.secondary">
              {missingReason}
            </Typography>
          )}
        </Box>
        {showIncludedReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            fontStyle="italic"
            sx={{ flexShrink: 0 }}
          >
            {getIncludedReason(item, documentTitle, isPayorRule, isRequired)}
          </Typography>
        )}
        {section === "missing" && !showCheckbox && (
          <Button
            variant="contained"
            size="small"
            color="secondary"
            onClick={() =>
              window.open(`/clients/${clientId}/assessments`, "_blank", "noopener,noreferrer")
            }
            sx={{ flexShrink: 0 }}
          >
            Go to {displayName}
          </Button>
        )}
      </Stack>
    );
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 11 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 7,
          py: 5,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <IconButton
            onClick={onBack}
            aria-label="Back"
            sx={{
              border: "1px solid",
              borderColor: "grey.300",
              borderRadius: "10px",
              boxShadow: 1,
              p: "5px",
            }}
          >
            <ArrowLeft size={20} />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h2">
              Review Assessments
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Choose the clinical assessments to include in the {documentTitle}.
            </Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} aria-label="Close dialog" size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 7, pb: 7, pt: 0 }}>
        <Stack sx={{ mt: 7, gap: 5 }}>
          {isOverridden && <OverrideChip />}
          {sections.map(({ section, title, iconColor, caption, items }) => (
            <Accordion
              key={section}
              expanded={expandedSections.has(section)}
              onChange={() => toggleExpanded(section)}
              disableGutters
              sx={{
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                "&:before": { display: "none" },
                "&.Mui-expanded": { m: 0 },
                "&, &:first-of-type, &:last-of-type": { borderRadius: "12px" },
              }}
            >
              <AccordionSummary>
                <Typography
                  variant="subtitle1"
                  sx={{ display: "flex", alignItems: "center", gap: 3, whiteSpace: "nowrap" }}
                >
                  <Box component="span" sx={{ color: iconColor, display: "inline-flex" }}>
                    <ChartBar size={20} weight="duotone" />
                  </Box>
                  {title}
                </Typography>
                {caption && (
                  <Typography variant="caption" sx={{ whiteSpace: "nowrap" }}>
                    {caption}
                  </Typography>
                )}
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Stack>
                  {items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 4, py: 3 }}>
                      No assessments in this section.
                    </Typography>
                  ) : section === "missing" ? (
                    <>
                      {items
                        .filter((i) => !payorRuleSet.has(i.type))
                        .map((item) => renderRow(item, section))}
                      {items.some((i) => payorRuleSet.has(i.type)) && (
                        <Alert severity="warning" variant="standard" sx={{ mx: 4, my: 3 }}>
                          {payorRequiredAssessments?.operator === "AND"
                            ? "This client's insurance requires all of the following assessments to be included."
                            : "This client's insurance requires at least one of the following assessments to be included."}
                        </Alert>
                      )}
                      {items
                        .filter((i) => payorRuleSet.has(i.type))
                        .map((item) => renderRow(item, section))}
                    </>
                  ) : (
                    items.map((item) => renderRow(item, section))
                  )}
                </Stack>
              </AccordionDetails>
            </Accordion>
          ))}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 7, pt: 5, pb: 5 }}>
        <Button
          variant="contained"
          color="primary"
          endIcon={<ArrowRight size={16} />}
          onClick={() => {
            onIncludedAssessmentsChange(
              Array.from(new Set(grouped.included.map((item) => item.type)))
            );
            onExcludedQabfIdsChange(Array.from(excludedQabfIds));
            onContinue();
          }}
          disabled={grouped.missing.length > 0 && !isOverridden}
        >
          Review Clinical Goals
        </Button>
      </DialogActions>
    </>
  );
};

export default ReviewAssessmentsStep;
